import { useEffect, useMemo, useRef, useState } from 'react';
import { formatCodeForDisplay, normaliseRecoveryCode } from '../game/codes';
import { mergeStates } from '../game/merge';
import { collectedCount, lifetimeScore } from '../game/scoring';
import { CATALOGUE } from '../data/plates';
import { useGame } from '../state/GameProvider';

const STATUS_TEXT: Record<string, string> = {
  synced: 'Backed up',
  pending: 'Changes waiting to back up',
  syncing: 'Backing up…',
  offline: 'Offline, will back up once you have signal',
  error: 'Could not back up',
  'local-only': 'Saved on this device only',
};

/**
 * The callback puts a slug in the fragment rather than a sentence, so the
 * wording lives here with the rest of the copy and is not baked into a redirect.
 */
const SIGN_IN_ERRORS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled.',
  'rate-limited': 'Too many sign-in attempts from this connection. Try again later.',
  'unknown-flow': 'That sign-in link has already been used. Try again.',
  'expired-flow': 'That sign-in took too long. Try again.',
  'bad-flow-binding': 'That sign-in could not be verified. Start again from this device.',
  'unknown-player': 'This device is not registered yet. Back up once, then sign in.',
  'not-configured': 'Sign-in is not set up on this deployment yet.',
};

const SIGN_IN_FALLBACK = 'Sign-in did not work. Try again.';

export function Settings() {
  const {
    state, sync, recoveryCode, authLink, pendingAdoption,
    setDisplayName, restoreFrom, resetEverything, syncNow,
    beginSignIn, completeSignIn, resolveSignIn, cancelSignIn, unlinkAccount,
  } = useGame();

  const [name, setName] = useState(state.displayName);
  const [codeInput, setCodeInput] = useState('');
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authNote, setAuthNote] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);

  const handledTicket = useRef(false);

  // ----------------------------------------------------- returning from Google
  //
  // The ticket comes home in the fragment, which never reaches a server and
  // never lands in a Referer. It is stripped from the address bar before
  // anything else happens, so a reload or a shared URL cannot replay it - and
  // because it is spent server-side on first use, a replay would fail anyway.
  useEffect(() => {
    if (handledTicket.current) return;
    if (!window.location.hash.startsWith('#')) return;

    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const ticket = fragment.get('auth');
    const failure = fragment.get('auth-error');
    if (!ticket && !failure) return;

    handledTicket.current = true;
    window.history.replaceState(null, '', window.location.pathname);

    if (failure) {
      setAuthError(SIGN_IN_ERRORS[failure] ?? SIGN_IN_FALLBACK);
      return;
    }
    if (!ticket) return;

    setAuthBusy(true);
    void (async () => {
      try {
        const result = await completeSignIn(ticket, fragment.get('email'));
        if (result.kind === 'linked') setAuthNote('Signed in. This collection is now recoverable.');
        if (result.kind === 'adopted') setAuthNote('Signed in. Your collection is back.');
        if (result.kind === 'conflict') {
          setAuthError(
            'This device is already connected to a different Google account. Disconnect it first.',
          );
        }
      } catch {
        setAuthError(SIGN_IN_FALLBACK);
      } finally {
        setAuthBusy(false);
      }
    })();
  }, [completeSignIn]);

  // The exact result of the merge, rather than a union of claim counts: an undo
  // on one side can beat a claim on the other, so counting keys would promise a
  // number the merge does not deliver.
  const mergedCount = useMemo(() => {
    if (!pendingAdoption) return 0;
    return Object.keys(mergeStates(state, pendingAdoption.payload).lifetime).length;
  }, [state, pendingAdoption]);

  const handleRestore = async () => {
    setRestoreError(null);

    if (!normaliseRecoveryCode(codeInput)) {
      setRestoreError('That is not a valid three-word code. Check the spelling.');
      return;
    }

    setRestoring(true);
    try {
      await restoreFrom(codeInput);
      setCodeInput('');
    } catch (error) {
      setRestoreError(
        error instanceof Error ? error.message : 'Could not restore that code.',
      );
    } finally {
      setRestoring(false);
    }
  };

  const handleReset = async () => {
    if (!confirmingReset) {
      setConfirmingReset(true);
      return;
    }
    await resetEverything();
    setConfirmingReset(false);
  };

  const handleUnlink = async () => {
    if (!confirmingUnlink) {
      setConfirmingUnlink(true);
      return;
    }
    setAuthError(null);
    setAuthBusy(true);
    try {
      await unlinkAccount();
      setAuthNote('Disconnected. Your collection is untouched.');
    } catch {
      setAuthError('Could not disconnect. Check your signal and try again.');
    } finally {
      setAuthBusy(false);
      setConfirmingUnlink(false);
    }
  };

  const chooseAdoption = async (choice: 'merge' | 'switch') => {
    setAuthBusy(true);
    try {
      await resolveSignIn(choice);
      setAuthNote(
        choice === 'merge' ? 'Merged. Nothing was lost.' : 'Switched to your account.',
      );
    } finally {
      setAuthBusy(false);
    }
  };

  return (
    <div className="screen">
      <h1 className="screen-title">Settings</h1>
      <p className="screen-sub">
        {collectedCount(state)} of {CATALOGUE.length} plates ·{' '}
        {lifetimeScore(state).toLocaleString()} lifetime points
      </p>

      {pendingAdoption && (
        <div className="card attention">
          <h2>Two collections</h2>
          <p>
            This device has {collectedCount(state)}{' '}
            {collectedCount(state) === 1 ? 'plate' : 'plates'}. The account you signed
            into has {Object.keys(pendingAdoption.payload.lifetime).length}. Merging keeps
            every plate from both and cannot be undone.
          </p>
          <button
            type="button"
            className="btn primary"
            disabled={authBusy}
            onClick={() => void chooseAdoption('merge')}
          >
            Merge both: {mergedCount} plates
          </button>
          <button
            type="button"
            className="btn"
            disabled={authBusy}
            onClick={() => void chooseAdoption('switch')}
          >
            Use my account&rsquo;s collection instead
          </button>
          <button type="button" className="btn" disabled={authBusy} onClick={cancelSignIn}>
            Cancel, stay signed out
          </button>
        </div>
      )}

      <div className="card">
        <h2>Your name</h2>
        <p>Shown to trip-mates and friends on their leaderboards.</p>
        <input
          className="search"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => setDisplayName(name)}
          maxLength={40}
          aria-label="Display name"
        />
      </div>

      <div className="card">
        <h2>Google account</h2>
        {authLink ? (
          <>
            <div className="status-line">
              <span className="status-dot synced" />
              <span>Signed in{authLink.email ? ` as ${authLink.email}` : ''}</span>
            </div>
            <p style={{ marginTop: 10 }}>
              Sign in with this account on a new phone and your collection comes back. We
              only store a reference to your account, never your email or name.
            </p>
            {confirmingUnlink && (
              recoveryCode ? (
                <>
                  <p>Write this down first, since it's the other way back in.</p>
                  <div className="code-display">{formatCodeForDisplay(recoveryCode)}</div>
                  <div style={{ height: 12 }} />
                </>
              ) : (
                <p className="field-error">
                  This device has no recovery code, because it got this collection by
                  signing in. Disconnecting leaves it reachable only from a device that
                  still has the code.
                </p>
              )
            )}
            <button
              type="button"
              className="btn danger"
              disabled={authBusy}
              onClick={() => void handleUnlink()}
            >
              {confirmingUnlink ? 'Tap again to disconnect' : 'Disconnect'}
            </button>
            {confirmingUnlink && (
              <button type="button" className="btn" onClick={() => setConfirmingUnlink(false)}>
                Cancel
              </button>
            )}
          </>
        ) : (
          <>
            <p>
              A sturdier way back than the three-word code: sign in on any phone and your
              collection follows. We only store a reference to your account, never your
              email or name.
            </p>
            <button
              type="button"
              className="btn primary"
              disabled={authBusy}
              onClick={beginSignIn}
            >
              {authBusy ? 'Signing in…' : 'Sign in with Google'}
            </button>
          </>
        )}
        {authError && <p className="field-error">{authError}</p>}
        {authNote && !authError && <p className="field-ok">{authNote}</p>}
      </div>

      <div className="card">
        <h2>Recovery code</h2>
        {recoveryCode ? (
          <>
            <p>
              Write this down. It is the only way to get your collection back on a new
              phone, and it is not stored anywhere we can read it.
            </p>
            <div className="code-display">{formatCodeForDisplay(recoveryCode)}</div>
          </>
        ) : authLink ? (
          <p style={{ marginBottom: 0 }}>
            This device got its collection by signing in, so it has no code of its own.
            Signing in again is how you get back here.
          </p>
        ) : (
          <>
            <p>
              Write this down. It is the only way to get your collection back on a new
              phone, and it is not stored anywhere we can read it.
            </p>
            <div className="empty" style={{ padding: '20px 0' }}>
              You will get a code the first time this device reaches the internet.
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Backup</h2>
        <div className="status-line">
          <span className={`status-dot ${sync.status}`} />
          <span>{STATUS_TEXT[sync.status] ?? sync.status}</span>
        </div>
        {sync.lastSyncedAt && (
          <p style={{ marginTop: 8, marginBottom: 12 }}>
            Last backed up {new Date(sync.lastSyncedAt).toLocaleTimeString('en-AU')}.
          </p>
        )}
        <div style={{ height: 10 }} />
        <button type="button" className="btn" onClick={() => void syncNow()}>
          Back up now
        </button>
      </div>

      <div className="card">
        <h2>Restore a collection</h2>
        <p>
          Enter a three-word code to pull that collection onto this device. This
          replaces what is currently here.
        </p>
        <input
          className="search"
          value={codeInput}
          onChange={(event) => setCodeInput(event.target.value)}
          placeholder="galah-jarrah-esky"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Recovery code"
        />
        {restoreError && <p className="field-error">{restoreError}</p>}
        <div style={{ height: 10 }} />
        <button
          type="button"
          className="btn"
          onClick={() => void handleRestore()}
          disabled={restoring || codeInput.trim().length === 0}
        >
          {restoring ? 'Restoring…' : 'Restore'}
        </button>
      </div>

      <div className="card">
        <h2>Start over</h2>
        <p>
          Wipes this device and begins a new collection. If you still have your
          recovery code you can always restore the old one.
        </p>
        <button type="button" className="btn danger" onClick={() => void handleReset()}>
          {confirmingReset ? 'Tap again to erase everything' : 'Erase this collection'}
        </button>
        {confirmingReset && (
          <button type="button" className="btn" onClick={() => setConfirmingReset(false)}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
